import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { GraduationCap, Plus, Edit, Trash2, Users, UserCheck, Eye, EyeOff, Calendar, Clock, UserPlus, UserX, Bell, Send, CheckCircle, XCircle, AlertCircle, X, Copy, MoreVertical } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminCursosPage() {
  const navigate = useNavigate();
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ open: false, curso: null });
  const [duplicandoId, setDuplicandoId] = useState(null);
  
  // Estado para recordatorios por curso
  const [enviandoCurso, setEnviandoCurso] = useState(null);
  const [reporteModal, setReporteModal] = useState({ open: false, reporte: null });

  useEffect(() => {
    fetchCursos();
  }, []);

  const fetchCursos = async () => {
    try {
      const res = await axios.get(`${API_URL}/capacitaciones/admin/cursos`);
      setCursos(res.data);
    } catch (err) {
      console.error('Error cargando cursos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEnviarRecordatoriosCurso = async (curso) => {
    if (!window.confirm(`¿Enviar recordatorios a los inscriptos de "${curso.nombre || curso.titulo}"?`)) {
      return;
    }
    
    setEnviandoCurso(curso.id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/capacitaciones/admin/cursos/${curso.id}/enviar-recordatorios`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 300000 // 5 minutos de timeout para envíos masivos
      });
      setReporteModal({ open: true, reporte: res.data });
    } catch (err) {
      console.error('Error enviando recordatorios:', err);
      
      // Detectar si fue timeout
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        alert('⏱️ El envío está tardando más de lo esperado.\n\nEs probable que los emails se estén enviando en segundo plano.\n\nHacé click en el botón 🔔 de nuevo en unos minutos para ver el reporte actualizado.');
      } else {
        alert(err.response?.data?.detail || 'Error al enviar recordatorios');
      }
    } finally {
      setEnviandoCurso(null);
    }
  };

  const handleToggleActivo = async (curso) => {
    try {
      await axios.patch(`${API_URL}/capacitaciones/admin/cursos/${curso.id}/toggle-activo`);
      fetchCursos();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleToggleInscripciones = async (curso) => {
    try {
      await axios.patch(`${API_URL}/capacitaciones/admin/cursos/${curso.id}/toggle-inscripciones`);
      fetchCursos();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.curso) return;
    try {
      await axios.delete(`${API_URL}/capacitaciones/admin/cursos/${deleteModal.curso.id}`);
      setDeleteModal({ open: false, curso: null });
      fetchCursos();
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleDuplicar = async (curso) => {
    if (!window.confirm(`¿Duplicar el curso "${curso.nombre}"? Se creará una copia como borrador (inactiva y con inscripciones cerradas) para que ajustes las fechas antes de publicarla.`)) {
      return;
    }
    setDuplicandoId(curso.id);
    try {
      const res = await axios.post(`${API_URL}/capacitaciones/admin/cursos/${curso.id}/duplicar`);
      navigate(`/admin/capacitaciones/cursos/${res.data.slug || res.data.id}/editar`);
    } catch (err) {
      console.error('Error duplicando curso:', err);
      alert(err.response?.data?.detail || 'Error al duplicar el curso');
    } finally {
      setDuplicandoId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' });
  };

  // Ordenar cursos: próximos primero, pasados/inactivos al final
  const cursosOrdenados = [...cursos].sort((a, b) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    const getFechaProxima = (curso) => {
      if (curso.fechas && curso.fechas.length > 0) {
        const fechasFuturas = curso.fechas
          .map(f => new Date(f))
          .filter(f => f >= hoy)
          .sort((x, y) => x - y);
        return fechasFuturas.length > 0 ? fechasFuturas[0] : null;
      }
      if (curso.fecha) return new Date(curso.fecha);
      return null;
    };
    
    const fechaA = getFechaProxima(a);
    const fechaB = getFechaProxima(b);
    
    const aActivo = a.activo !== false && fechaA && fechaA >= hoy;
    const bActivo = b.activo !== false && fechaB && fechaB >= hoy;
    
    if (aActivo && !bActivo) return -1;
    if (!aActivo && bActivo) return 1;
    
    if (fechaA && fechaB) return fechaA - fechaB;
    if (fechaA && !fechaB) return -1;
    if (!fechaA && fechaB) return 1;
    
    return 0;
  });

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Gestión de Cursos</h1>
          <p className="text-gray-600 text-sm sm:text-base">Administrá las capacitaciones del Colegio</p>
        </div>
        <Link to="/admin/capacitaciones/cursos/nuevo">
          <Button className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Curso
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <GraduationCap className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{cursos.length}</p>
                <p className="text-sm text-gray-500">Cursos totales</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{cursos.filter(c => c.activo).length}</p>
                <p className="text-sm text-gray-500">Cursos activos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Users className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{cursos.reduce((sum, c) => sum + (c.inscriptos_activos || c.total_inscriptos || 0), 0)}</p>
                <p className="text-sm text-gray-500">Inscriptos activos</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-asistentes-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <UserCheck className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="stat-asistentes-valor">{cursos.reduce((sum, c) => sum + (c.asistentes || 0), 0)}</p>
                <p className="text-sm text-gray-500">Asistentes</p>
                {(() => {
                  const totalInsc = cursos.reduce((s, c) => s + (c.inscriptos_activos || c.total_inscriptos || 0), 0);
                  const totalAsist = cursos.reduce((s, c) => s + (c.asistentes || 0), 0);
                  if (totalInsc === 0) return null;
                  const pct = Math.round((totalAsist / totalInsc) * 100);
                  return <p className="text-xs text-purple-600 font-medium">{pct}% de los inscriptos</p>;
                })()}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      )}

      {/* Empty */}
      {!loading && cursosOrdenados.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No hay cursos creados</p>
            <Link to="/admin/capacitaciones/cursos/nuevo">
              <Button>Crear primer curso</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Courses List */}
      {!loading && cursosOrdenados.length > 0 && (
        <div className="space-y-4">
          {cursosOrdenados.map((curso) => (
            <Card key={curso.id} className={!curso.activo ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  {/* Image */}
                  <div className="w-full sm:w-32 h-32 sm:h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    {curso.imagen ? (
                      <img 
                        src={curso.imagen} 
                        alt="" 
                        className="w-full h-full object-cover"
                        style={{
                          objectPosition: curso.imagen_focal_point 
                            ? `${curso.imagen_focal_point.x}% ${curso.imagen_focal_point.y}%`
                            : 'center'
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <GraduationCap className="w-8 h-8 text-gray-300" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 break-words">{curso.nombre}</h3>
                        <p className="text-sm text-gray-500 line-clamp-2">{curso.descripcion_corta}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {curso.activo ? (
                          <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">Activo</span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">Inactivo</span>
                        )}
                        {curso.inscripciones_abiertas !== false ? (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">Inscripciones abiertas</span>
                        ) : (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Inscripciones cerradas</span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {curso.horario_a_coordinar ? (
                          <span className="text-emerald-600 font-medium">A coordinar</span>
                        ) : (
                          formatDate(curso.fecha)
                        )}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {curso.duracion_horas || '-'}h
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {curso.inscriptos_activos ?? curso.total_inscriptos ?? 0} inscriptos
                        {curso.inscriptos_cancelados > 0 && (
                          <span className="text-gray-400 text-xs">({curso.inscriptos_cancelados} canc.)</span>
                        )}
                      </span>
                    </div>

                    {/* Actions - Mobile friendly (patrón kebab como Eventos) */}
                    <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t sm:border-t-0 sm:mt-0 sm:pt-0 w-full sm:w-auto">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 sm:flex-initial"
                        onClick={() => navigate(`/admin/capacitaciones/cursos/${curso.id}/inscriptos`)}
                        data-testid={`btn-inscriptos-${curso.id}`}
                      >
                        <Users className="w-4 h-4 mr-1" />
                        <span>Inscriptos</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 sm:flex-initial"
                        onClick={() => navigate(`/admin/capacitaciones/cursos/${curso.id}/editar`)}
                        data-testid={`btn-editar-${curso.id}`}
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        <span>Editar</span>
                      </Button>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`btn-menu-curso-${curso.id}`}
                            aria-label="Más acciones"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem
                            onClick={() => handleEnviarRecordatoriosCurso(curso)}
                            disabled={enviandoCurso === curso.id}
                            data-testid={`btn-enviar-recordatorios-${curso.id}`}
                          >
                            {enviandoCurso === curso.id ? (
                              <>
                                <div className="w-4 h-4 mr-2 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
                                Enviando...
                              </>
                            ) : (
                              <>
                                <Bell className="w-4 h-4 mr-2 text-amber-600" />
                                Enviar recordatorios
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleInscripciones(curso)}>
                            {curso.inscripciones_abiertas !== false ? (
                              <>
                                <UserX className="w-4 h-4 mr-2" />
                                Cerrar inscripciones
                              </>
                            ) : (
                              <>
                                <UserPlus className="w-4 h-4 mr-2" />
                                Abrir inscripciones
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleActivo(curso)}>
                            {curso.activo ? (
                              <>
                                <EyeOff className="w-4 h-4 mr-2" />
                                Desactivar curso
                              </>
                            ) : (
                              <>
                                <Eye className="w-4 h-4 mr-2" />
                                Activar curso
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDuplicar(curso)}
                            disabled={duplicandoId === curso.id}
                            data-testid={`btn-duplicar-curso-${curso.id}`}
                          >
                            {duplicandoId === curso.id ? (
                              <>
                                <div className="w-4 h-4 mr-2 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                                Duplicando...
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4 mr-2 text-indigo-600" />
                                Duplicar
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600 focus:text-red-700"
                            onClick={() => setDeleteModal({ open: true, curso })}
                            data-testid={`btn-eliminar-${curso.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar curso
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, curso: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Curso</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            ¿Estás seguro de eliminar el curso <strong>{deleteModal.curso?.nombre}</strong>? 
            Esta acción no se puede deshacer y se eliminarán todas las inscripciones asociadas.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, curso: null })}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reporte de Recordatorios Modal */}
      <Dialog open={reporteModal.open} onOpenChange={(open) => setReporteModal({ open, reporte: null })}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-600" />
              Reporte de Envío de Recordatorios
            </DialogTitle>
          </DialogHeader>
          
          {reporteModal.reporte && (
            <div className="space-y-4">
              {/* Envío en background */}
              {reporteModal.reporte.background && (
                <div className="bg-blue-50 border border-blue-300 rounded-lg p-4" data-testid="recordatorios-bg-banner">
                  <div className="flex items-start gap-3">
                    <Bell className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-blue-800">📨 Envío en curso en segundo plano</p>
                      <p className="text-sm text-blue-700 mt-1">
                        Se encolaron <strong>{reporteModal.reporte.resumen?.encolados || 0}</strong> recordatorios. Como son muchos, el envío sigue corriendo en el servidor y puede tardar varios minutos.
                      </p>
                      <p className="text-sm text-blue-700 mt-2">
                        Volvé a abrir esta ventana en unos minutos para ver el conteo actualizado (los ya enviados aparecen como "Ya enviados").
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Alerta de límite alcanzado */}
              {reporteModal.reporte.limite_alcanzado && (
                <div className="bg-amber-100 border border-amber-400 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-800">⚠️ Límite de Gmail alcanzado</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Se enviaron {reporteModal.reporte.resumen?.enviados_ahora || 0} emails antes de alcanzar el límite diario de Gmail.
                        {reporteModal.reporte.pendientes_por_limite?.length > 0 && (
                          <span> Quedan <strong>{reporteModal.reporte.pendientes_por_limite.length}</strong> inscriptos pendientes de envío.</span>
                        )}
                      </p>
                      <p className="text-sm text-amber-700 mt-2">
                        <strong>Solución:</strong> Cambiá la cuenta de envío desde Configuración (a la otra cuenta) y volvé a intentar, o esperá hasta mañana.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Curso */}
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-medium">{reporteModal.reporte.curso?.titulo}</p>
                <p className="text-sm text-gray-500">Total inscriptos: {reporteModal.reporte.total_inscriptos}</p>
              </div>
              
              {/* Resumen */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div className="bg-green-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-700">{reporteModal.reporte.resumen?.enviados_ahora || 0}</p>
                  <p className="text-xs text-green-600">Enviados ahora</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-700">{reporteModal.reporte.resumen?.ya_enviados_antes || 0}</p>
                  <p className="text-xs text-blue-600">Ya enviados</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-700">{reporteModal.reporte.resumen?.cancelados || 0}</p>
                  <p className="text-xs text-gray-600">Cancelados</p>
                </div>
                <div className="bg-red-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-700">{reporteModal.reporte.resumen?.errores || 0}</p>
                  <p className="text-xs text-red-600">Errores</p>
                </div>
                {(reporteModal.reporte.resumen?.pendientes_por_limite || 0) > 0 && (
                  <div className="bg-amber-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-700">{reporteModal.reporte.resumen?.pendientes_por_limite || 0}</p>
                    <p className="text-xs text-amber-600">Pendientes</p>
                  </div>
                )}
              </div>
              
              {/* Enviados ahora */}
              {reporteModal.reporte.enviados?.length > 0 && (
                <div>
                  <h4 className="font-medium text-green-700 flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4" />
                    Enviados ahora ({reporteModal.reporte.enviados.length})
                  </h4>
                  <div className="bg-green-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    {reporteModal.reporte.enviados.map((item, i) => (
                      <p key={i} className="text-sm text-green-800">
                        {item.nombre} - {item.email}
                      </p>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Ya enviados antes */}
              {reporteModal.reporte.ya_enviados?.length > 0 && (
                <div>
                  <h4 className="font-medium text-blue-700 flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4" />
                    Ya habían recibido recordatorio ({reporteModal.reporte.ya_enviados.length})
                  </h4>
                  <div className="bg-blue-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    {reporteModal.reporte.ya_enviados.slice(0, 10).map((item, i) => (
                      <p key={i} className="text-sm text-blue-800">
                        {item.nombre} - {item.email}
                      </p>
                    ))}
                    {reporteModal.reporte.ya_enviados.length > 10 && (
                      <p className="text-sm text-blue-600 font-medium mt-1">
                        ... y {reporteModal.reporte.ya_enviados.length - 10} más
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Errores */}
              {reporteModal.reporte.errores?.length > 0 && (
                <div>
                  <h4 className="font-medium text-red-700 flex items-center gap-2 mb-2">
                    <XCircle className="w-4 h-4" />
                    Errores ({reporteModal.reporte.errores.length})
                  </h4>
                  <div className="bg-red-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    {reporteModal.reporte.errores.map((item, i) => (
                      <p key={i} className="text-sm text-red-800">
                        {item.nombre} - {item.email}: <span className="text-red-600">{item.error}</span>
                      </p>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Cancelados */}
              {reporteModal.reporte.cancelados?.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-700 flex items-center gap-2 mb-2">
                    <AlertCircle className="w-4 h-4" />
                    Inscripciones canceladas ({reporteModal.reporte.cancelados.length})
                  </h4>
                  <div className="bg-gray-100 rounded-lg p-3 max-h-24 overflow-y-auto">
                    {reporteModal.reporte.cancelados.slice(0, 5).map((item, i) => (
                      <p key={i} className="text-sm text-gray-600">
                        {item.nombre} - {item.email}
                      </p>
                    ))}
                    {reporteModal.reporte.cancelados.length > 5 && (
                      <p className="text-sm text-gray-500 mt-1">
                        ... y {reporteModal.reporte.cancelados.length - 5} más
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Pendientes por límite de Gmail */}
              {reporteModal.reporte.pendientes_por_limite?.length > 0 && (
                <div>
                  <h4 className="font-medium text-amber-700 flex items-center gap-2 mb-2">
                    <Clock className="w-4 h-4" />
                    Pendientes de envío ({reporteModal.reporte.pendientes_por_limite.length})
                  </h4>
                  <div className="bg-amber-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    {reporteModal.reporte.pendientes_por_limite.slice(0, 10).map((item, i) => (
                      <p key={i} className="text-sm text-amber-800">
                        {item.nombre} - {item.email}
                      </p>
                    ))}
                    {reporteModal.reporte.pendientes_por_limite.length > 10 && (
                      <p className="text-sm text-amber-600 font-medium mt-1">
                        ... y {reporteModal.reporte.pendientes_por_limite.length - 10} más
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setReporteModal({ open: false, reporte: null })}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
